import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from 'framer-motion';
import { useLocale } from '@/lib/i18n';
import { useSectionTranslation } from '@/hooks';
import { heroTranslations } from './translations';
import type { HeroBanner } from './useMarketplaceHero';

const AUTO_ADVANCE_MS = 6000;

interface BannerCarouselProps {
  banners: HeroBanner[];
}

export function BannerCarousel({ banners }: BannerCarouselProps) {
  const { direction } = useLocale();
  const t = useSectionTranslation(heroTranslations);
  const isRTL = direction === 'rtl';
  const reducedMotion = useReducedMotion();

  // Banners are only renderable once an image has been uploaded.
  const slides = banners.filter((b) => b.imageUrl);
  const slideCount = slides.length;

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const activeIndex = slideCount > 0 ? index % slideCount : 0;

  useEffect(() => {
    if (reducedMotion || paused || slideCount <= 1) return;
    const id = setInterval(() => {
      setIndex((current) => (current + 1) % slideCount);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [reducedMotion, paused, slideCount]);

  if (slideCount === 0) return null;

  const banner = slides[activeIndex];
  const overlayStyle = {
    backgroundImage: isRTL
      ? 'linear-gradient(to left, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.3) 55%, transparent 100%)'
      : 'linear-gradient(to right, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.3) 55%, transparent 100%)',
  };

  return (
    <LazyMotion features={domAnimation}>
      <div
        className="relative grid w-full overflow-hidden rounded-2xl lg:rounded-3xl"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <AnimatePresence initial={false}>
          <m.div
            key={activeIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="[grid-area:1/1]"
          >
            <img
              src={banner.imageUrl}
              alt={banner.headline}
              /* Heights match HeroSkeleton exactly. `lg:aspect-auto` with
                 `h-auto` used to let the image's intrinsic ratio decide the
                 desktop height, so any banner that was not already21:9 grew
                 the hero to its full natural size. */
              className="block h-[220px] sm:h-[280px] lg:h-[340px] w-full object-cover"
            />
          </m.div>
        </AnimatePresence>

        <div className="pointer-events-none absolute inset-0" style={overlayStyle} />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent" />

        <div className="absolute bottom-0 start-0 p-4 sm:p-6 md:p-8">
              {banner.logoUrl && (
                <img src={banner.logoUrl} alt="" className="h-6 w-auto mb-2 sm:h-8 sm:mb-3" loading="lazy" />
              )}
              <h2 className="font-display font-bold text-xl sm:text-3xl md:text-5xl text-white tracking-tight">
                {banner.headline}
              </h2>
              {banner.subtext && (
                <p className="text-xs sm:text-sm text-white/80 mt-1.5 sm:mt-2 max-w-md">{banner.subtext}</p>
              )}
              {banner.ctaLabel &&
                (banner.linkUrl.startsWith('/') ? (
                  <Link to={banner.linkUrl} className="btn-primary mt-3 sm:mt-4 h-9 sm:h-10 px-4 sm:px-5 text-xs sm:text-sm">
                    {banner.ctaLabel}
                  </Link>
                ) : (
                  <a
                    href={banner.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary mt-3 sm:mt-4 h-9 sm:h-10 px-4 sm:px-5 text-xs sm:text-sm"
                  >
                    {banner.ctaLabel}
                  </a>
                ))}
            </div>

        {slideCount > 1 && (
          <div className="absolute bottom-3 inset-x-0 z-10 flex items-center justify-center gap-1.5">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={t.carousel.slideLabel.replace('{index}', String(i + 1))}
                aria-current={i === activeIndex}
                className={`h-1 rounded-full transition-all ${
                  i === activeIndex ? 'w-6 bg-accent' : 'w-3 bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </LazyMotion>
  );
}
